import { useMemo, useRef, useState } from 'react';
import type { TableProps } from 'antd';
import {
  CloudUploadOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileSearchOutlined,
  PushpinFilled,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';

import { DataTable, MenuThaoTac, PageHeader } from '../../../components/shared';
import { Badge, Button, Empty, Input, Select } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useRoutePermission } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { dungLuongVN, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { useToast } from '../../../lib/toast';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import { UserCell } from '../../xu-ly/components/UserCell';
import { useTenNguoi } from '../../xu-ly/hooks/useTenNguoi';
import { HopDocLai } from '../components/HopDocLai';
import { HopTaiLen } from '../components/HopTaiLen';
import { NganChiTietTep } from '../components/NganChiTietTep';
import { TRANG_THAI_TEP } from '../constants';
import { useDanhSachKhoOcr, useGhiKhoOcr } from '../hooks/useKhoOcr';
import { phanTramDoc } from '../nghiep-vu/kho-ocr';
import { OCR_FILE_STATES } from '../types';
import type { OcrFile, OcrFileState } from '../types';

type LocTrangThai = OcrFileState | 'tat-ca';

/** Chờ người dùng ngừng gõ rồi mới gửi tìm lên server. */
const CHO_GO = 350;

/**
 * Kho bản OCR (cổng tenant, `/xu-ly/tai-lieu-ocr`, phân hệ `OCR_FILES`; module `ocr` đợt 4):
 * tài liệu đã đọc chữ để tạo chứng từ mà không OCR lại. Lọc ở server (`search`, `state`), phân
 * trang con trỏ bằng `Tải thêm`; còn tài liệu đang đọc thì danh sách tự hỏi lại.
 */
export function KhoOcrView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const tenNguoi = useTenNguoi();
  const coTao = useRoutePermission('CREATE');
  const coXoa = useRoutePermission('DELETE');
  const tieuDe = t('nav.muc.quanLyTaiLieuOcr');

  const [tuKhoa, setTuKhoa] = useState('');
  const [tim, setTim] = useState('');
  const hen = useRef<number | undefined>(undefined);
  const [trangThai, setTrangThai] = useState<LocTrangThai>('tat-ca');
  const [chiTiet, setChiTiet] = useState<string | null>(null);
  const [lanMoTai, setLanMoTai] = useState(0);
  const [moTai, setMoTai] = useState(false);
  const [docLai, setDocLai] = useState<OcrFile | null>(null);
  const [xoa, setXoa] = useState<OcrFile | null>(null);
  const ghi = useGhiKhoOcr();

  const q = useDanhSachKhoOcr({ search: tim || undefined, state: trangThai === 'tat-ca' ? undefined : trangThai });
  const dong = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const coLoc = tim !== '' || trangThai !== 'tat-ca';
  const dangTai = q.isLoading;

  const goTim = (v: string) => {
    setTuKhoa(v);
    window.clearTimeout(hen.current);
    hen.current = window.setTimeout(() => setTim(v.trim()), v ? CHO_GO : 0);
  };

  const moTaiLen = () => {
    setLanMoTai((n) => n + 1);
    setMoTai(true);
  };

  const xacNhanXoa = () => {
    if (!xoa) return;
    const ten = xoa.file_name ?? '';
    ghi.xoaTep.mutate(xoa.id, {
      onSuccess: () => {
        toast.success(t('khoOcr.daXoaTep', { ten }));
        if (chiTiet === xoa.id) setChiTiet(null);
        setXoa(null);
      },
    });
  };

  const columns: TableProps<OcrFile>['columns'] = [
    {
      title: t('khoOcr.cot.tep'),
      width: 320,
      render: (_v, f) => (
        <button
          type="button"
          onClick={() => setChiTiet(f.id)}
          className="flex min-w-0 max-w-xs flex-col text-left"
        >
          <span className="truncate text-sm font-medium text-content-primary" title={f.file_name ?? ''}>
            {f.file_name ?? t('chung.khongCo')}
          </span>
          <span className="truncate text-2xs text-content-muted">
            {dungLuongVN(f.file_size, locale)} · {f.mime_type ?? t('chung.khongCo')}
          </span>
        </button>
      ),
    },
    {
      title: t('khoOcr.cot.trangThai'),
      width: 190,
      render: (_v, f) => {
        const cau = TRANG_THAI_TEP[f.state];
        const chay = f.running;
        const pt = chay ? phanTramDoc(chay) : null;
        return (
          <div className="min-w-[140px] max-w-[200px]">
            <Badge variant={cau.variant}>{t(cau.khoa)}</Badge>
            {chay && (
              <>
                <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-subtle">
                  <div className="h-full rounded-full bg-primary-500" style={{ width: `${pt ?? 5}%` }} />
                </div>
                <p className="mt-1 truncate text-2xs text-content-muted">
                  {chay.page_count
                    ? t('khoOcr.tienDo', { xong: chay.pages_done ?? 0, tong: chay.page_count })
                    : t('khoOcr.tienDoChuaRo')}
                </p>
              </>
            )}
          </div>
        );
      },
    },
    {
      title: t('khoOcr.cot.banDung'),
      width: 200,
      render: (_v, f) =>
        f.effective ? (
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-xs text-content-primary">
              {f.effective.is_text_layer ? t('ocr.lopVanBanPdf') : (f.effective.reader_label ?? f.effective.reader_key)}
            </span>
            <span className="inline-flex items-center gap-1 text-2xs text-content-muted">
              {f.effective.pinned_at && <PushpinFilled aria-hidden />}
              {t(f.effective.pinned_at ? 'khoOcr.daGhim' : 'khoOcr.moiNhat')}
            </span>
          </div>
        ) : (
          <span className="text-xs text-content-muted">{t('khoOcr.chuaCoBanDung')}</span>
        ),
    },
    {
      title: t('khoOcr.cot.soBan'),
      dataIndex: 'version_count',
      width: 90,
      align: 'right',
      render: (n: number) => <span className="text-xs tabular-nums text-content-secondary">{soVN(n, locale)}</span>,
    },
    {
      title: t('khoOcr.cot.soChungTu'),
      dataIndex: 'attached_count',
      width: 110,
      align: 'right',
      render: (n: number) => <span className="text-xs tabular-nums text-content-secondary">{soVN(n, locale)}</span>,
    },
    {
      title: t('khoOcr.cot.nguoiTai'),
      width: 180,
      render: (_v, f) => <UserCell name={tenNguoi(f.created_by) ?? ''} />,
    },
    {
      title: t('khoOcr.cot.ngayTai'),
      dataIndex: 'created_at',
      width: 150,
      render: (v: string) => (
        <span className="whitespace-nowrap text-xs tabular-nums text-content-muted">{ngayGioVN(v, locale)}</span>
      ),
    },
    {
      title: t('chung.thaoTac'),
      width: 72,
      align: 'center',
      fixed: 'right',
      render: (_v, f) => (
        <MenuThaoTac
          tenBanGhi={f.file_name ?? ''}
          muc={[
            {
              khoa: 'xem',
              nhan: t('khoOcr.xemChiTiet'),
              icon: <EyeOutlined />,
              nhom: 'xem',
              onChon: () => setChiTiet(f.id),
            },
            {
              khoa: 'doc-lai',
              nhan: t('khoOcr.docLai'),
              icon: <ReloadOutlined />,
              hien: coTao && !f.running,
              onChon: () => setDocLai(f),
            },
            {
              khoa: 'xoa',
              nhan: t('khoOcr.xoaTaiLieu'),
              icon: <DeleteOutlined />,
              nhom: 'pha-huy',
              hien: coXoa,
              voHieu: Boolean(f.delete_blocked_reason),
              moTa: f.delete_blocked_reason ?? undefined,
              onChon: () => setXoa(f),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.xuLyTaiLieu') }, { label: tieuDe }]}
          titleIcon={<FileSearchOutlined className="text-primary-600" />}
          title={tieuDe}
          subtitle={t('khoOcr.moTa')}
          actions={
            <>
              <Button
                variant="ghost"
                size="md"
                title={t('chung.lamMoi')}
                aria-label={t('chung.lamMoi')}
                onClick={() => void q.refetch()}
              >
                <ReloadOutlined className={q.isFetching ? 'animate-spin' : ''} />
              </Button>
              {coTao && (
                <Button variant="primary" size="md" icon={<CloudUploadOutlined />} onClick={moTaiLen}>
                  {t('khoOcr.taiLen')}
                </Button>
              )}
            </>
          }
        />

        <div className="flex flex-wrap items-center gap-2.5 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
          <Input
            allowClear
            className="w-full sm:w-80"
            prefix={<SearchOutlined className="text-content-muted" />}
            placeholder={t('khoOcr.timPlaceholder')}
            aria-label={t('khoOcr.timNhan')}
            value={tuKhoa}
            onChange={(e) => goTim(e.target.value)}
          />
          <Select<LocTrangThai>
            className="w-full sm:w-56"
            aria-label={t('khoOcr.locTrangThai')}
            value={trangThai}
            onChange={setTrangThai}
            options={[
              { value: 'tat-ca', label: t('khoOcr.tatCaTrangThai') },
              ...OCR_FILE_STATES.map((s) => ({ value: s, label: t(TRANG_THAI_TEP[s].khoa) })),
            ]}
          />
        </div>

        <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          {q.error && !q.data ? (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <span className="min-w-0 text-sm text-danger-700">
                {t('khoOcr.loiDanhSach', { loi: dichLoi(q.error) })}
              </span>
              <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
                {t('chung.thuLai')}
              </Button>
            </div>
          ) : (
            <>
              <DataTable<OcrFile>
                caption={t('khoOcr.bangCaption')}
                rowKey="id"
                dataSource={dong}
                columns={columns}
                loading={dangTai}
                size="middle"
                pagination={false}
                scroll={{ x: 'max-content' }}
                locale={{
                  emptyText: dangTai ? (
                    ' '
                  ) : (
                    <Empty
                      icon={FileSearchOutlined}
                      // Rỗng vì bộ lọc: bỏ mô tả và lời mời hành động.
                      title={coLoc ? t('khoOcr.rongLoc') : t('khoOcr.rongThat')}
                      description={coLoc ? undefined : t('khoOcr.rongThatMo')}
                      action={
                        coLoc || !coTao ? undefined : (
                          <Button variant="primary" size="md" icon={<CloudUploadOutlined />} onClick={moTaiLen}>
                            {t('khoOcr.taiLenDauTien')}
                          </Button>
                        )
                      }
                    />
                  ),
                }}
              />
              {dong.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-default px-4 py-3">
                  <span className="text-xs text-content-muted" role="status">
                    {t(q.hasNextPage ? 'khoOcr.dangHienCon' : 'khoOcr.dangHien', { so: soVN(dong.length, locale) })}
                  </span>
                  {q.hasNextPage && (
                    <Button
                      variant="outline"
                      size="sm"
                      loading={q.isFetchingNextPage}
                      onClick={() => void q.fetchNextPage()}
                    >
                      {t('chung.taiThem')}
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <HopTaiLen key={lanMoTai} open={moTai} onClose={() => setMoTai(false)} />
      <HopDocLai key={docLai?.id ?? 'dong'} tep={docLai} onClose={() => setDocLai(null)} />
      <NganChiTietTep id={chiTiet} onClose={() => setChiTiet(null)} onDocLai={setDocLai} />
      <HopXacNhanThaoTac
        open={xoa !== null}
        tieuDe={t('khoOcr.xoaTepTieuDe', { ten: xoa?.file_name ?? '' })}
        than={t('khoOcr.xoaTepMo')}
        nhanXacNhan={t('khoOcr.xoaTaiLieu')}
        nguyHiem
        dangChay={ghi.xoaTep.isPending}
        loi={ghi.xoaTep.error ? dichLoi(ghi.xoaTep.error) : null}
        onClose={() => {
          setXoa(null);
          ghi.xoaTep.reset();
        }}
        onXacNhan={xacNhanXoa}
      />
    </div>
  );
}
